import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography, Divider, Link } from '@mui/material';
import { Google } from '@mui/icons-material';
import { API_URL } from '../utils/apiConfig';
import { setOAuthPostAuthRedirect } from '../utils/oauthPostAuthRedirect';
import { persistSignupPostAuthRedirectPath } from '../utils/signupAuthRedirect';

const OAUTH_CONSENT_KEY = 'oauthLegalConsent';

export const setOAuthLegalConsent = ({ documentIds, method, clickedAt }) => {
  if (documentIds?.length) {
    sessionStorage.setItem(
      OAUTH_CONSENT_KEY,
      JSON.stringify({
        documentIds,
        method: method || 'unknown',
        clickedAt: clickedAt || new Date().toISOString(),
      })
    );
  } else {
    sessionStorage.removeItem(OAUTH_CONSENT_KEY);
  }
};

export const getOAuthLegalConsent = () => {
  try {
    const raw = sessionStorage.getItem(OAUTH_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { documentIds: parsed, method: 'signup_checkbox', clickedAt: null };
    }
    return parsed;
  } catch {
    return null;
  }
};

export const getOAuthConsentDocumentIds = () => getOAuthLegalConsent()?.documentIds || null;

export const clearOAuthLegalConsent = () => {
  sessionStorage.removeItem(OAUTH_CONSENT_KEY);
};

export const clearOAuthConsentDocumentIds = () => {
  clearOAuthLegalConsent();
};

const SocialLoginButtons = ({
  disabled = false,
  variant = 'login',
  referralCode = '',
  consentGranted = false,
  consentDocumentIds = [],
  embedded = false,
  hideLegalCaption = false,
  hideDivider = false,
  docsLoading = false,
  googleButtonLabel,
  legalCaptionVerb,
  postAuthRedirect = '/dashboard',
}) => {
  const isSignup = variant === 'signup';

  const handleSocialLogin = (provider) => {
    if (disabled || docsLoading) return;
    if (isSignup && !consentGranted) return;
    if (isSignup && consentDocumentIds.length === 0) return;

    const clickedAt = new Date().toISOString();
    if (isSignup) {
      setOAuthLegalConsent({
        documentIds: consentDocumentIds,
        method: 'signup_checkbox',
        clickedAt,
      });
    } else if (consentDocumentIds.length > 0) {
      setOAuthLegalConsent({
        documentIds: consentDocumentIds,
        method: 'login_clickwrap',
        clickedAt,
      });
    } else {
      clearOAuthLegalConsent();
    }

    setOAuthPostAuthRedirect(postAuthRedirect);
    persistSignupPostAuthRedirectPath(postAuthRedirect);

    const ref = typeof referralCode === 'string' ? referralCode.trim() : '';
    const refParam = ref ? `?ref=${encodeURIComponent(ref)}` : '';
    window.location.href = `${API_URL}/api/auth/${provider}${refParam}`;
  };

  const buttonText = googleButtonLabel || (isSignup ? 'Sign up with' : 'Sign in with');
  const dividerVerb = googleButtonLabel ? 'continue' : (isSignup ? 'sign up' : 'sign in');
  const captionVerb = legalCaptionVerb || (isSignup ? 'signing up' : 'signing in');
  const socialDisabled =
    disabled
    || docsLoading
    || (isSignup && !consentGranted)
    || (isSignup && consentDocumentIds.length === 0);

  return (
    <Box sx={{ width: '100%' }}>
      {!hideDivider && (
        <Divider sx={{ my: embedded ? 2 : 3 }}>
          <Typography variant="body2" color="text.secondary">
            Or {dividerVerb} with
          </Typography>
        </Divider>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          type="button"
          fullWidth
          variant="outlined"
          size="large"
          disabled={socialDisabled}
          onClick={() => handleSocialLogin('google')}
          startIcon={<Google />}
          sx={{
            py: 1.5,
            borderColor: '#4285F4',
            color: '#4285F4',
            '&:hover': {
              borderColor: '#3367D6',
              backgroundColor: 'rgba(66, 133, 244, 0.04)',
            },
            '&:disabled': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {buttonText} Google
        </Button>
      </Box>

      {isSignup && !consentGranted && !docsLoading && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Please check all required agreements above to enable Google signup.
        </Typography>
      )}

      {isSignup && consentGranted && consentDocumentIds.length === 0 && !docsLoading && (
        <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Legal documents could not be loaded. Refresh the page or use email signup.
        </Typography>
      )}

      {!(hideLegalCaption && isSignup) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 2,
            lineHeight: 1.4,
          }}
        >
          By {captionVerb} with a social account, you agree to our{' '}
          <Link
            component={RouterLink}
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            color="primary"
            fontWeight={600}
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            component={RouterLink}
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            color="primary"
            fontWeight={600}
          >
            Privacy Policy
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default SocialLoginButtons;
